import { createSlice } from '@reduxjs/toolkit'
import {
  addCompany,
  companyChangeStatus,
  editCompany,
  lastTenant,
  retrieveCompanies,
  retrieveCompaniesModules,
  retrieveCompaniesFabsTenants,
  ssoAddCompanyConfig,
  ssoEditCompanyConfig,
  ssoGetCompanyConfig,
  companiesResetAlertHandler,
  retrieveCompanyDetails,
} from './companiesThunk'
import { objNotEmpty, updateStateFromQueries } from '../../../utils/helpers'

const defaultPageSize = 9
const defaultActiveSummary = 'ALL'

const initialState = {
  loading: {
    fetch: false,
    mutate: false,
  },
  pageNumber: 0,
  pageSize: defaultPageSize,
  finalPage: false,
  summaries: {},
  activeSummary: defaultActiveSummary,
  sortBy: [],
  activeSort: {},
  activeSearch: '',
  lastTenant: null,
  value: [],
  data: [],
  companyModules: [],
  fabsTenants: [],
  ssoCompanyConfig: {},
  alert: {},
  companyDetails: {},
}

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(companiesResetAlertHandler.fulfilled, (state) => {
      state.alert = {}
    })
    builder.addCase(retrieveCompanies.pending, (state, { meta: { arg: { queries } } }) => {
      state.loading.fetch = true
      state.finalPage = true
      state.pageNumber = queries?.pageNumber ? queries?.pageNumber : 0
      if (!queries?.pageNumber) {
        state.data = []
      }
    })
    builder.addCase(retrieveCompanies.fulfilled, (state, { payload }) => {
      const { response, queries } = payload
      state.loading.fetch = false
      state.data = queries?.pageNumber === 0
        ? response.companies : [...state.data, ...response.companies]
      state.summaries = response.metadata
      state.finalPage = response.companies.length < state.pageSize
      state.sortBy = response.sort
      state.activeSort =
        (objNotEmpty(state.activeSort) && state.pageNumber >= 1)
          ? state.activeSort : response?.sort?.[0]
      state.activeSearch = queries?.activeSearch ?? state.activeSearch
      updateStateFromQueries(state, queries)
    })
    builder.addCase(retrieveCompanies.rejected, (state, { payload }) => {
      state.loading.fetch = false
      state.alert = payload ?? {}
    })
    builder.addCase(retrieveCompanyDetails.fulfilled, (state, { payload }) => {
      const { response, id } = payload
      state.companyDetails = { id, ...response }
      state.loading.fetch = false
    })
    builder.addCase(retrieveCompanyDetails.pending, (state, { meta: { arg: { id } } }) => {
      state.loading.fetch = true
      state.companyDetails = { id, data: null }
    })
    builder.addCase(retrieveCompanyDetails.rejected, (state, {
      payload, meta: { arg: { id } } }) => {
      state.loading.fetch = false
      state.companyDetails = { id, data: null }
      state.alert = payload
    })
    builder.addCase(addCompany.fulfilled, (state) => {
      state.loading.mutate = false
    })
    builder.addCase(addCompany.pending, (state) => {
      state.loading.mutate = true
      state.finalPage = true
    })
    builder.addCase(addCompany.rejected, (state, { payload }) => {
      state.loading.mutate = false
      state.alert = payload
    })
    builder.addCase(editCompany.fulfilled, (state) => {
      state.loading.mutate = false
    })
    builder.addCase(editCompany.pending, (state) => {
      state.loading.mutate = true
      state.finalPage = true
    })
    builder.addCase(editCompany.rejected, (state, { payload }) => {
      state.loading.mutate = false
      state.alert = payload
    })
    builder.addCase(ssoAddCompanyConfig.fulfilled, (state) => {
      state.loading.mutate = false
    })
    builder.addCase(ssoAddCompanyConfig.pending, (state) => {
      state.loading.mutate = true
    })
    builder.addCase(ssoAddCompanyConfig.rejected, (state, { payload }) => {
      state.loading.mutate = false
      state.alert = payload
    })
    builder.addCase(ssoEditCompanyConfig.fulfilled, (state) => {
      state.loading.mutate = false
    })
    builder.addCase(ssoEditCompanyConfig.pending, (state) => {
      state.loading.mutate = true
    })
    builder.addCase(ssoEditCompanyConfig.rejected, (state, { payload }) => {
      state.loading.mutate = false
      state.alert = payload
    })
    builder.addCase(companyChangeStatus.pending, (state) => {
      state.loading.mutate = true
      state.finalPage = true
    })
    builder.addCase(companyChangeStatus.fulfilled, (state) => {
      state.pageNumber = 0
      state.loading.mutate = false
    })
    builder.addCase(companyChangeStatus.rejected, (state) => {
      state.loading.mutate = false
    })
    builder.addCase(lastTenant.fulfilled, (state, { payload }) => {
      state.lastTenant = payload
      state.loading.mutate = false
    })
    builder.addCase(lastTenant.pending, (state) => {
      state.loading.mutate = true
      state.finalPage = true
    })
    builder.addCase(lastTenant.rejected, (state) => {
      state.loading.mutate = false
    })
    builder.addCase(retrieveCompaniesModules.fulfilled, (state, { payload }) => {
      state.companyModules = payload
      state.loading.mutate = false
    })
    builder.addCase(retrieveCompaniesModules.pending, (state) => {
      state.loading.mutate = true
      state.finalPage = true
    })
    builder.addCase(retrieveCompaniesModules.rejected, (state) => {
      state.loading.mutate = false
    })
    builder.addCase(retrieveCompaniesFabsTenants.pending, (state) => {
      state.loading.mutate = true
      state.finalPage = true
    })
    builder.addCase(retrieveCompaniesFabsTenants.rejected, (state) => {
      state.loading.mutate = false
    })
    builder.addCase(retrieveCompaniesFabsTenants.fulfilled, (state, { payload }) => {
      state.fabsTenants = payload
      state.loading.mutate = false
    })
    builder.addCase(ssoGetCompanyConfig.fulfilled, (state, { payload }) => {
      state.ssoCompanyConfig = payload
      state.loading.mutate = false
    })
    builder.addCase(ssoGetCompanyConfig.pending, (state) => {
      state.loading.mutate = true
    })
    builder.addCase(ssoGetCompanyConfig.rejected, (state, { payload }) => {
      state.loading.mutate = false
      state.alert = payload
    })
  },
})

export const selectCompanies = ({ companies }) => companies

export default companiesSlice.reducer
